/**
 * transferenciasResumen.ts — Cálculo puro del cuadre por transferencias.
 *
 * Reglas:
 *  - 100% pura: no hace fetch, no toca localStorage, no depende de hooks.
 *  - Recibe ventas en el shape `Venta` (ya filtradas por sesión por quien la llame).
 *  - Agrupa por slot inmutable (`digital_1`, `tarjeta`, etc.), no por nombre editable.
 *  - El total esperado en cuadre usa neto en cuenta (post tarifa) cuando la venta
 *    guardó snapshot de comisión; el cobrado bruto queda en totalCobrado.
 */
import type {
  MetodoCuadre,
  ResumenTransferencias,
  TransferenciaItem,
  Venta,
} from "../types/caja.types";
import type { PaymentMethodsConfig } from "@/lib/paymentMethodsConfig";
import {
  aggregateVentasByPaymentSlot,
  getSlotDisplayLabel,
  isCuadreSlot,
  sortSlotIds,
} from "./paymentMethodAggregation";
import { extractNetPaymentLinesFromVenta } from "./paymentMethodUsageFee";

function aggregateNetBySlot(ventas: Venta[]): Map<
  string,
  {
    totalNeto: number;
    totalCobrado: number;
    totalTarifas: number;
    count: number;
    labelsSeen: Set<string>;
    items: TransferenciaItem[];
  }
> {
  const map = new Map<
    string,
    {
      totalNeto: number;
      totalCobrado: number;
      totalTarifas: number;
      count: number;
      labelsSeen: Set<string>;
      items: TransferenciaItem[];
    }
  >();

  ventas.forEach((venta) => {
    if (venta.estado === "anulada" || venta.estado === "devolucion-total") {
      return;
    }

    extractNetPaymentLinesFromVenta(venta).forEach((line) => {
      const gross = Math.round(line.gross);
      if (gross <= 0) return;

      const net = Math.round(line.net);
      const tarifa = Math.max(0, gross - net);
      const labelSnap = venta.detalleMetodoPago?.breakdown?.find(
        (b, idx) =>
          b.method === line.slotId &&
          (line.breakdownIndex == null || idx === line.breakdownIndex),
      )?.methodLabel;

      const existing = map.get(line.slotId) ?? {
        totalNeto: 0,
        totalCobrado: 0,
        totalTarifas: 0,
        count: 0,
        labelsSeen: new Set<string>(),
        items: [],
      };

      if (labelSnap?.trim()) existing.labelsSeen.add(labelSnap.trim());
      else if (venta.metodoPagoLabel?.trim()) {
        existing.labelsSeen.add(venta.metodoPagoLabel.trim());
      }

      const itemId =
        line.breakdownIndex != null
          ? `${venta.id}#${line.breakdownIndex}`
          : venta.id;

      existing.items.push({
        id: itemId,
        ventaId: venta.id,
        fecha: venta.fecha,
        monto: gross,
        montoNeto: tarifa > 0 ? net : undefined,
        tarifaUso: tarifa > 0 ? tarifa : undefined,
        userName: venta.userName,
      });
      existing.count += 1;
      existing.totalCobrado += gross;
      existing.totalNeto += net;
      existing.totalTarifas += tarifa;
      map.set(line.slotId, existing);
    });
  });

  return map;
}

/**
 * Calcula los cobros por método de transferencia/tarjeta (excluye efectivo).
 *
 * Para ventas mixtas, cada split del breakdown se contabiliza como UNA
 * transacción del slot correspondiente (un comprobante = un item).
 * El total esperado refleja lo que debe aparecer en la cuenta (neto).
 */
export function calcResumenTransferencias(
  ventas: Venta[],
  cfg?: PaymentMethodsConfig | null,
): ResumenTransferencias {
  const aggregated = aggregateNetBySlot(ventas);
  const resumen: ResumenTransferencias = {};

  [...aggregated.entries()]
    .filter(([slotId]) => isCuadreSlot(slotId, cfg))
    .sort(([a], [b]) => sortSlotIds(a, b))
    .forEach(([slotId, agg]) => {
      const display = getSlotDisplayLabel(slotId, agg.labelsSeen, cfg);
      const entry: MetodoCuadre = {
        method: slotId,
        label: display.label,
        renamedDuringShift: display.renamedDuringShift,
        labelVariants: display.labelVariants,
        kind: display.kind === "tarjeta" ? "tarjeta" : "digital",
        total: agg.totalNeto,
        count: agg.count,
        items: agg.items,
      };
      if (agg.totalTarifas > 0) {
        entry.totalCobrado = agg.totalCobrado;
        entry.totalTarifas = agg.totalTarifas;
      }
      resumen[slotId] = entry;
    });

  return resumen;
}

/** Devuelve la lista de slots que tienen al menos una transacción en cuadre. */
export function metodosConActividad(resumen: ResumenTransferencias): string[] {
  return Object.keys(resumen).filter((m) => (resumen[m]?.count ?? 0) > 0);
}

/** @deprecated Preferir aggregateNetBySlot. Mantener compat con tests viejos. */
export { aggregateVentasByPaymentSlot };
